'use client';

import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Globe } from 'lucide-react';

interface Language {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
}

const languages: Language[] = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳' },
  { code: 'mr', name: 'Marathi', nativeName: 'मराठी', flag: '🇮🇳' },
  { code: 'bn', name: 'Bengali', nativeName: 'বাংলা', flag: '🇮🇳' },
  { code: 'ta', name: 'Tamil', nativeName: 'தமிழ்', flag: '🇮🇳' },
  { code: 'te', name: 'Telugu', nativeName: 'తెలుగు', flag: '🇮🇳' },
  { code: 'kn', name: 'Kannada', nativeName: 'ಕನ್ನಡ', flag: '🇮🇳' },
  { code: 'ml', name: 'Malayalam', nativeName: 'മലയാളം', flag: '🇮🇳' },
  { code: 'gu', name: 'Gujarati', nativeName: 'ગુજરાતી', flag: '🇮🇳' },
  { code: 'pa', name: 'Punjabi', nativeName: 'ਪੰਜਾਬੀ', flag: '🇮🇳' },
  { code: 'or', name: 'Odia', nativeName: 'ଓଡ଼ିଆ', flag: '🇮🇳' },
];

interface LanguageSelectorProps {
  compact?: boolean;
}

export default function LanguageSelector({ compact = false }: LanguageSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<Language>(languages[0]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (lang: Language) => {
    setSelected(lang);
    setIsOpen(false);
    // BACKEND INTEGRATION: Update user language preference via i18n context or Supabase profile
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-500 transition-all"
        style={{
          background: 'rgba(15,30,53,0.8)',
          border: '1px solid rgba(30,58,90,0.6)',
          color: 'var(--muted-foreground)',
        }}
        aria-label="Select language"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <Globe size={12} />
        {!compact && (
          <span className="hidden sm:block">{selected.nativeName}</span>
        )}
        <span>{selected.flag}</span>
        <ChevronDown
          size={12}
          className="transition-transform duration-200"
          style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
      </button>

      {isOpen && (
        <div
          className="absolute right-0 top-full mt-1.5 w-44 rounded-xl overflow-hidden animate-slide-up z-50"
          style={{
            background: 'var(--navy-700)',
            border: '1px solid var(--navy-500)',
            boxShadow: '0 16px 40px rgba(5,13,26,0.7)',
          }}
          role="listbox"
          aria-label="Available languages"
        >
          <div className="p-1.5">
            <p className="text-2xs font-600 text-muted-foreground tracking-widest uppercase px-2 py-1">
              Platform Language
            </p>
            {languages.map((lang) => (
              <button
                key={`lang-option-${lang.code}`}
                onClick={() => handleSelect(lang)}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs transition-all"
                style={{
                  background: selected.code === lang.code ? 'rgba(201,168,76,0.1)' : 'transparent',
                  color: selected.code === lang.code ? 'var(--gold-500)' : 'var(--muted-foreground)',
                }}
                role="option"
                aria-selected={selected.code === lang.code}
              >
                <span className="text-sm">{lang.flag}</span>
                <span className="flex-1 text-left">{lang.nativeName}</span>
                {selected.code === lang.code && (
                  <span className="text-2xs text-primary">✓</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}